/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ProductDimensionType } from '@msdyn365-commerce/retail-proxy';
import { Button, Popover } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

/**
 * Resources for the product dimensions matrix dimension selection component.
 */
export interface IProductDimensionsMatrixDimensionSelectionResources {
    productDimensionTypeColor: string;
    productDimensionTypeConfiguration: string;
    productDimensionTypeSize: string;
    productDimensionTypeStyle: string;
}

/**
 * Props for the product dimensions matrix dimension selection component.
 */
export interface IProductDimensionsMatrixDimensionSelectionProps {
    className: string;

    resources: IProductDimensionsMatrixDimensionSelectionResources;

    dimensionTypes: number[];
    currentDimensionType: number;

    onDimensionTypeChange(previousDimensionType: number, newDimensionType: number): void;
}

/**
 * Retrieves dimension localized name.
 * @param dimensionType - Dimension type.
 * @param resources - Resources to use to retrieve localized text.
 * @returns Localized dimension name.
 */
export const getDimensionName = (dimensionType: number, resources: IProductDimensionsMatrixDimensionSelectionResources): string => {
    switch (dimensionType) {
        case ProductDimensionType.Color:
            return resources.productDimensionTypeColor;
        case ProductDimensionType.Configuration:
            return resources.productDimensionTypeConfiguration;
        case ProductDimensionType.Size:
            return resources.productDimensionTypeSize;
        case ProductDimensionType.Style:
            return resources.productDimensionTypeStyle;
        default:
            return '';
    }
};

/**
 * Props for the dimension item component.
 */
interface IProductDimensionsMatrixDimensionItemProps {
    className: string;

    resources: IProductDimensionsMatrixDimensionSelectionResources;

    dimensionType: number;

    onDimensionTypeSelect(dimensionType: number): void;
}

/**
 * Dimension item component which represents a single dimension from the list of dimensions.
 * @param props - Component props.
 * @returns React node.
 */
const ProductDimensionsMatrixDimensionItemComponent: React.FC<IProductDimensionsMatrixDimensionItemProps> = (
    props: IProductDimensionsMatrixDimensionItemProps) => {

    const changeCurrentDimension = React.useCallback(() => {
        props.onDimensionTypeSelect(props.dimensionType);
    }, [props]);

    const title = getDimensionName(props.dimensionType, props.resources);
    return (
        <Button
            key={props.dimensionType}
            className={props.className}
            title={title}
            onClick={changeCurrentDimension}
        >
            {title}
        </Button>
    );
};

/**
 * Dimension selection component for product dimensions matrix.
 * @param props - Component props.
 * @returns React node.
 */
export const ProductDimensionsMatrixDimensionSelectionComponent: React.FC<IProductDimensionsMatrixDimensionSelectionProps> = (
    props: IProductDimensionsMatrixDimensionSelectionProps) => {

    const buttonTogglingReference = React.createRef<HTMLButtonElement>();
    const [isPopoverShown, setIsPopoverShown] = React.useState(false);
    const [currentDimensionType, setCurrentDimensionType] = React.useState(props.currentDimensionType);
    const [currentDimensionTypeLabel, setCurrentDimensionTypeLabel] = React.useState(getDimensionName(props.currentDimensionType, props.resources));

    const popoverClassName = `${props.className}__popover`;

    const togglePopoverHandler = React.useCallback(() => {
        setIsPopoverShown(!isPopoverShown);
    }, [isPopoverShown]);

    const changeCurrentDimension = React.useCallback((dimensionType: number) => {
        props.onDimensionTypeChange(currentDimensionType, dimensionType);
        setCurrentDimensionType(dimensionType);
        setCurrentDimensionTypeLabel(getDimensionName(dimensionType, props.resources));
    }, [currentDimensionType, props]);

    return (
        <div className={props.className}>
            <Button
                className={`${props.className}__toggle-button`}
                title={currentDimensionTypeLabel}
                innerRef={buttonTogglingReference}
                aria-expanded={isPopoverShown}
                aria-describedby={props.className}
                onClick={togglePopoverHandler}
            >
                {currentDimensionTypeLabel}
            </Button>
            <Popover
                className={popoverClassName}
                placement='bottom-start'
                isOpen={isPopoverShown}
                target={buttonTogglingReference}
                toggle={togglePopoverHandler}
            >
                <div className={`${popoverClassName}__content`}>
                    {
                        props.dimensionTypes.map(dimensionType => {
                            return (
                                <ProductDimensionsMatrixDimensionItemComponent
                                    key={dimensionType}
                                    className={`${popoverClassName}__content__item`}
                                    resources={props.resources}
                                    dimensionType={dimensionType}
                                    onDimensionTypeSelect={changeCurrentDimension}
                                />
                            );
                        })
                    }
                </div>
            </Popover>
        </div>
    );
};

